import React, { useState, useRef } from "react";
import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { IoPersonCircle } from "react-icons/io5";
import { MdCameraAlt } from "react-icons/md";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { rerenderLayout } from "@/store/RenderProtectionRoute.slice";
import { AppConfig } from "@/utils/AppConfig";
import { getMaxImageSize } from "@/utils/helpers";

const { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_MB_String } = getMaxImageSize(
  AppConfig.ImagesMaxSizeInMB.profileImage,
);

interface ProfileImageProps {
  imageUrl: string | null;
  size: number;
  withUpdate?: boolean;
  setNeedRefetch?: React.Dispatch<React.SetStateAction<boolean>>;
  iconClassName?: string;
}

const ProfileImage: React.FC<ProfileImageProps> = ({
  imageUrl = "",
  withUpdate = false,
  size = 130,
  setNeedRefetch,
  iconClassName,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const cropperRef = useRef<ReactCropperElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(
        t("userSettings.fileSizeExceeded", {
          maxSize:
            locale === "ar"
              ? MAX_IMAGE_SIZE_MB_String.ar
              : MAX_IMAGE_SIZE_MB_String.en,
        }),
      );
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setTempImage(reader.result as string);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = ""; // Reset input
  };

  async function uploadImage(file: File) {
    const url = `${BASE_URL}/api/dashboard/users/avatar?locale=${locale}`;
    const toastId = toast.loading(t("userSettings.uploading"));
    setIsUploading(true);

    try {
      const response = await axios.put(
        url,
        { attachment: file },
        {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        toast.success(t("userSettings.uploadSuccess"));

        if (setNeedRefetch) {
          setNeedRefetch(true);
        }
        dispatch(rerenderLayout());
      } else {
        toast.error(response.data.error || t("userSettings.uploadFailed"));
      }
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.error || t("userSettings.uploadFailed")
          : t("userSettings.unexpectedError"),
      );
    } finally {
      toast.dismiss(toastId);
      setIsUploading(false);
    }
  }

  async function removeImage() {
    const url = `${BASE_URL}/api/dashboard/users/avatar?locale=${locale}`;
    const toastId = toast.loading(t("userSettings.removing"));
    setIsUploading(true);

    try {
      const response = await axios.put(
        url,
        { attachment: "" },
        {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        toast.success(t("userSettings.removeSuccess"));

        if (setNeedRefetch) {
          setNeedRefetch(true);
        }
        dispatch(rerenderLayout());
      } else {
        toast.error(response.data.error || t("userSettings.removeFailed"));
      }
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.error || t("userSettings.removeFailed")
          : t("userSettings.unexpectedError"),
      );
    } finally {
      toast.dismiss(toastId);
      setIsUploading(false);
    }
  }

  const handleCrop = () => {
    if (cropperRef.current?.cropper) {
      const cropper = cropperRef.current.cropper;
      const canvas = cropper.getCroppedCanvas({
        width: size,
        height: size,
        fillColor: "#fff",
      });

      if (canvas) {
        canvas?.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], "profile-image.webp", {
                type: "image/webp",
              });
              uploadImage(file);
            }
          },
          "image/webp",
          0.8,
        );
      }
    }
    setCropperOpen(false);
  };

  const cancelCrop = () => {
    setCropperOpen(false);
  };

  const triggerFileInput = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="relative">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />

      {/* Profile Image Display */}
      <div className="group relative">
        {imageUrl ? (
          <div
            className="overflow-hidden rounded-full border-2 border-gray-200"
            style={{ width: size, height: size }}
          >
            <img
              src={imageUrl}
              alt="Profile"
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <IoPersonCircle
            size={size}
            className={`rounded-full text-gray-400 ${iconClassName}`}
          />
        )}

        {withUpdate && !isUploading && (
          <div
            onClick={triggerFileInput}
            className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black bg-opacity-30 opacity-0 transition-opacity group-hover:opacity-100"
            style={{ width: size, height: size }}
          >
            <MdCameraAlt size={size * 0.3} className="text-white" />
          </div>
        )}

        {isUploading && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-30"
            style={{ width: size, height: size }}
          >
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-white"></div>
          </div>
        )}
      </div>

      {withUpdate && imageUrl && (
        <button
          type="button"
          onClick={removeImage}
          className="mt-3 w-full rounded-md bg-red-500 px-2 py-1 text-sm font-medium text-white"
        >
          {t("userSettings.removeImage")}
        </button>
      )}

      {/* Cropper Modal */}
      {cropperOpen && (
        <div className="fixed inset-0 z-[99999999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white p-6">
            <div className="mb-4 h-96 w-full">
              <Cropper
                src={tempImage || ""}
                style={{ height: "100%", width: "100%" }}
                aspectRatio={1}
                viewMode={2}
                guides={false}
                ref={cropperRef}
                autoCropArea={1}
                background={false}
                responsive={true}
                checkOrientation={false}
              />
            </div>

            <div className="flex justify-center gap-4">
              <button
                onClick={handleCrop}
                className="rounded bg-sidebar px-4 py-2 text-skyblue disabled:bg-blue-300"
                disabled={isUploading}
              >
                {isUploading
                  ? t("userSettings.uploading")
                  : t("userSettings.save")}
              </button>
              <button
                onClick={cancelCrop}
                className="rounded bg-red-500 px-4 py-2 text-white"
                disabled={isUploading}
              >
                {t("userSettings.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileImage;
